import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Briefcase, FileDown, Loader2, LogOut, Megaphone, Mail, Plus, Reply, Search, ShieldCheck, StickyNote, Trash2, Users } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "enquiries", label: "Hiring Enquiries", icon: Briefcase },
  { id: "candidates", label: "Candidate Profiles", icon: Users },
  { id: "contacts", label: "Contact Messages", icon: Mail },
  { id: "jobs", label: "Job Openings", icon: Megaphone },
  { id: "visitors", label: "Visitors", icon: BarChart3 },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const get = async (path) => {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error("load failed");
  return r.json();
};

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || "Login failed");
        navigate("/admin", { replace: true, state: { user: body } });
      } catch (e) {
        toast.error(e.message || "Login failed");
        navigate("/admin", { replace: true });
      }
    })();
  }, [location.hash, navigate]);

  return (
    <div className="grid min-h-[80vh] place-items-center pt-24">
      <Loader2 className="animate-spin text-sky-400" size={28} />
    </div>
  );
}

function AdminInner() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [tab, setTab] = useState("enquiries");
  const [data, setData] = useState({ enquiries: [], candidates: [], contacts: [], jobs: [] });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (authState !== null) return;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setUser(await res.json());
        setAuthState(true);
      } catch {
        setAuthState(false);
      }
    })();
  }, [authState]);

  useEffect(() => {
    if (authState !== true) return;
    setLoading(true);
    (async () => {
      try {
        const [enquiries, candidates, contacts, jobs] = await Promise.all([
          get("/admin/enquiries"),
          get("/admin/candidates"),
          get("/admin/contacts"),
          get("/admin/jobs"),
        ]);
        setData({ enquiries, candidates, contacts, jobs });
        get("/admin/stats").then(setStats).catch(() => {});
      } catch {
        toast.error("Could not load dashboard data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [authState]);

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setAuthState(false);
    toast.success("Signed out.");
  };

  const downloadResume = async (file, name) => {
    try {
      const res = await fetch(`${API}/admin/resumes/${file}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = name || file;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download resume.");
    }
  };

  const CSV_COLS = {
    enquiries: [["created_at", "Date"], ["name", "Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["requirement", "Requirement"], ["positions", "Positions"], ["location", "Location"], ["message", "Message"]],
    candidates: [["created_at", "Date"], ["full_name", "Name"], ["email", "Email"], ["phone", "Phone"], ["location", "Location"], ["preferred_location", "Preferred Location"], ["experience", "Experience"], ["role", "Role"], ["expertise", "Expertise"], ["status", "Status"], ["resume_name", "Resume"]],
    contacts: [["created_at", "Date"], ["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["message", "Message"]],
    jobs: [["created_at", "Date"], ["title", "Title"], ["category", "Category"], ["location", "Location"], ["experience", "Experience"], ["status", "Status"], ["description", "Description"]],
  };

  const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["shortlisted", "Shortlisted"], ["placed", "Placed"]];
  const STATUS_CLS = {
    new: "border-white/15 text-slate-300",
    contacted: "border-sky-400/40 text-sky-300",
    shortlisted: "border-amber-400/40 text-amber-300",
    placed: "border-emerald-400/40 text-emerald-300",
  };

  const exportCSV = () => {
    if (!CSV_COLS[tab]) {
      toast.error("Nothing to export on this tab.");
      return;
    }
    const rows = data[tab];
    if (!rows.length) {
      toast.error("Nothing to export yet.");
      return;
    }
    const cols = CSV_COLS[tab];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      cols.map(([, l]) => l).join(","),
      ...rows.map((r) =>
        cols.map(([k]) => esc(k === "created_at" ? fmt(r[k]) : r[k] || (k === "status" ? "New" : ""))).join(",")
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `talentify-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  };

  const setStatus = async (id, status) => {
    const prev = data.candidates;
    setData((d) => ({ ...d, candidates: d.candidates.map((c) => (c.id === id ? { ...c, status } : c)) }));
    try {
      const res = await fetch(`${API}/admin/candidates/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Marked as ${status}.`);
    } catch {
      setData((d) => ({ ...d, candidates: prev }));
      toast.error("Could not update status.");
    }
  };

  const ENQUIRY_STATUSES = [["new", "New"], ["contacted", "Contacted"], ["in_progress", "In Progress"], ["closed", "Closed"]];
  const ENQUIRY_STATUS_CLS = {
    new: "border-white/15 text-slate-300",
    contacted: "border-sky-400/40 text-sky-300",
    in_progress: "border-indigo-400/40 text-indigo-300",
    closed: "border-emerald-400/40 text-emerald-300",
  };

  const [noteModal, setNoteModal] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const updateEnquiry = async (id, status) => {
    const prev = data.enquiries;
    setData((d) => ({ ...d, enquiries: d.enquiries.map((c) => (c.id === id ? { ...c, status } : c)) }));
    try {
      const res = await fetch(`${API}/admin/enquiries/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Marked as ${status.replace("_", " ")}.`);
    } catch {
      setData((d) => ({ ...d, enquiries: prev }));
      toast.error("Could not update status.");
    }
  };

  const saveNote = async () => {
    if (!noteModal) return;
    const { tab: t, row, note } = noteModal;
    try {
      const res = await fetch(`${API}/admin/${t}/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notes: note }),
      });
      if (!res.ok) throw new Error();
      setData((d) => ({ ...d, [t]: d[t].map((r) => (r.id === row.id ? { ...r, notes: note } : r)) }));
      setNoteModal(null);
      toast.success("Note saved.");
    } catch {
      toast.error("Could not save note.");
    }
  };

  const deleteRow = async (t, id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      const res = await fetch(`${API}/admin/${t}/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      setData((d) => ({ ...d, [t]: d[t].filter((r) => r.id !== id) }));
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const JOB_STATUSES = [["open", "Open"], ["closed", "Closed"]];
  const [jobModal, setJobModal] = useState(null);
  const [jobSaving, setJobSaving] = useState(false);

  const saveJob = async () => {
    if (!jobModal?.title?.trim()) {
      toast.error("Job title is required.");
      return;
    }
    setJobSaving(true);
    try {
      const res = await fetch(`${API}/admin/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(jobModal),
      });
      if (!res.ok) throw new Error();
      const body = await res.json();
      setData((d) => ({
        ...d,
        jobs: [{ ...jobModal, id: body.id, status: "open", created_at: new Date().toISOString() }, ...d.jobs],
      }));
      setJobModal(null);
      toast.success("Job posted — it's live on the Jobs page.");
    } catch {
      toast.error("Could not post job.");
    } finally {
      setJobSaving(false);
    }
  };

  const toggleJob = async (id, status) => {
    const prev = data.jobs;
    setData((d) => ({ ...d, jobs: d.jobs.map((j) => (j.id === id ? { ...j, status } : j)) }));
    try {
      const res = await fetch(`${API}/admin/jobs/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(status === "open" ? "Job reopened." : "Job closed — hidden from the public page.");
    } catch {
      setData((d) => ({ ...d, jobs: prev }));
      toast.error("Could not update job.");
    }
  };

  const rowActions = (t, r, i) => (
    <div className="flex items-center gap-2">
      <a
        data-testid={`reply-btn-${t}-${i}`}
        href={`mailto:${r.email}?subject=${encodeURIComponent(
          `Talentify — Re: ${t === "candidates" ? "your profile submission" : t === "enquiries" ? `your hiring requirement${r.company ? ` at ${r.company}` : ""}` : "your message"}`
        )}`}
        aria-label={`Reply to ${r.full_name || r.name} by email`}
        title="Reply by email"
        className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-500 transition-colors duration-300 hover:border-emerald-400/50 hover:text-emerald-300"
      >
        <Reply size={13} />
      </a>
      <button
        data-testid={`note-btn-${t}-${i}`}
        onClick={() => setNoteModal({ tab: t, row: r, note: r.notes || "" })}
        aria-label="Edit private note"
        title={r.notes ? "Note saved — click to edit" : "Add a private note"}
        className={`grid h-8 w-8 place-items-center rounded-lg border transition-colors duration-300 ${
          r.notes ? "border-amber-400/40 text-amber-300" : "border-white/10 text-slate-500 hover:border-sky-400/40 hover:text-sky-400"
        }`}
      >
        <StickyNote size={13} />
      </button>
      <button
        data-testid={`delete-btn-${t}-${i}`}
        onClick={() => deleteRow(t, r.id)}
        aria-label="Delete submission"
        className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-500 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );

  if (authState === null) {
    return (
      <div className="grid min-h-[80vh] place-items-center pt-24">
        <Loader2 className="animate-spin text-sky-400" size={28} />
      </div>
    );
  }

  if (authState === false) {
    return (
      <main className="grid min-h-[85vh] place-items-center px-6 pb-16 pt-28">
        <div className="glass-card w-full max-w-md rounded-3xl p-10 text-center">
          <span className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-2xl border border-sky-400/25 bg-sky-400/[0.07] text-sky-400">
            <ShieldCheck size={24} />
          </span>
          <h1 data-testid="admin-login-heading" className="font-display text-2xl font-bold tracking-tight text-white">
            Talentify Admin
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Private dashboard for enquiries, candidate profiles and resumes. Sign in with your authorised Google account.
          </p>
          <button
            data-testid="admin-google-login-btn"
            onClick={startGoogleLogin}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-900 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-slate-100"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81Z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24Z" />
              <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1Z" />
              <path fill="#EA4335" d="M12 4.76c1.76 0 3.35.6 4.59 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76Z" />
            </svg>
            Sign in with Google
          </button>
        </div>
      </main>
    );
  }

  const q = query.trim().toLowerCase();
  const matchQ = (r) => !q || Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(q));
  const matchS = (r) => statusFilter === "all" || tab === "contacts" || (r.status || "new") === statusFilter;
  const rows = tab === "visitors" ? [] : data[tab].filter((r) => matchQ(r) && matchS(r));

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pb-24 pt-32" data-testid="admin-dashboard">
      <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sky-400">Admin Dashboard</p>
          <h1 className="mt-2 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="mt-1 text-xs text-slate-500">{user?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-export-csv-btn"
            onClick={exportCSV}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-sky-400/50 hover:text-sky-300"
          >
            <FileDown size={15} /> Export CSV
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300"
          >
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => { setTab(t.id); setQuery(""); setStatusFilter("all"); }}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === t.id ? "bg-blue-600 text-white" : "border border-white/10 text-slate-400 hover:text-white"
            }`}
          >
            <t.icon size={15} />
            {t.label}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${tab === t.id ? "bg-white/20" : "bg-white/[0.06]"}`}>
              {t.id === "visitors" ? (stats ? stats.week : "…") : (data[t.id] || []).length}
            </span>
          </button>
        ))}
      </div>

      {tab !== "visitors" && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company, role..."
              className="input-dark pl-10"
              aria-label="Search submissions"
            />
          </div>
          {tab !== "contacts" && (
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              className="input-dark cursor-pointer appearance-none !w-auto"
            >
              <option value="all" className="bg-[#0A0F1D]">All statuses</option>
              {(tab === "candidates" ? STATUSES : tab === "jobs" ? JOB_STATUSES : ENQUIRY_STATUSES).map(([v, l]) => (
                <option key={v} value={v} className="bg-[#0A0F1D]">{l}</option>
              ))}
            </select>
          )}
          <span data-testid="admin-result-count" className="font-mono text-[11px] text-slate-500">
            {rows.length} of {data[tab].length}
          </span>
        </div>
      )}

      <div className="glass-card overflow-x-auto rounded-3xl" data-testid={`admin-table-${tab}`}>
        {tab === "visitors" ? (
          <div className="p-6 sm:p-8" data-testid="visitors-panel">
            {!stats ? (
              <div className="grid place-items-center py-20">
                <Loader2 className="animate-spin text-sky-400" size={26} />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[
                    { label: "Total Page Views", value: stats.total, id: "total" },
                    { label: "Views Today", value: stats.today, id: "today" },
                    { label: "Views Last 7 Days", value: stats.week, id: "week" },
                  ].map((c) => (
                    <div key={c.id} data-testid={`visitor-stat-${c.id}`} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
                      <p className="font-display text-3xl font-bold tracking-tight text-white">{c.value}</p>
                      <p className="mt-1 text-xs text-slate-500">{c.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Top Pages</h3>
                    <div className="space-y-3">
                      {stats.top_pages.length === 0 && <p className="text-sm text-slate-500">No visits tracked yet — they will appear here as people browse your site.</p>}
                      {stats.top_pages.map((p) => (
                        <div key={p.path} className="flex items-center gap-3">
                          <span className="w-36 truncate font-mono text-xs text-slate-300">{p.path}</span>
                          <div className="h-1.5 flex-1 rounded-full bg-white/[0.05]">
                            <div className="h-1.5 rounded-full bg-gradient-to-r from-sky-400 to-indigo-500" style={{ width: `${Math.max(6, (p.views / stats.top_pages[0].views) * 100)}%` }} />
                          </div>
                          <span className="w-10 text-right font-mono text-xs text-slate-400">{p.views}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Last 7 Days</h3>
                    <div className="flex h-44 items-end gap-2.5">
                      {stats.per_day.map((d) => {
                        const max = Math.max(...stats.per_day.map((x) => x.views), 1);
                        return (
                          <div key={d.day} className="flex flex-1 flex-col items-center justify-end gap-2">
                            <span className="font-mono text-[10px] text-slate-400">{d.views}</span>
                            <div className="w-full rounded-t-md bg-gradient-to-t from-sky-500/40 to-indigo-500/70" style={{ height: `${Math.max(4, (d.views / max) * 120)}px` }} />
                            <span className="font-mono text-[9px] text-slate-600">{d.day.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="animate-spin text-sky-400" size={26} />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-20 text-center text-sm text-slate-500">No submissions yet.</p>
        ) : tab === "enquiries" ? (
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {["Date", "Name", "Company", "Email", "Phone", "Requirement", "Positions", "Location", "Message", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/[0.05] text-slate-300 transition-colors hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{fmt(r.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-white">{r.name}</td>
                  <td className="px-5 py-4">{r.company}</td>
                  <td className="px-5 py-4"><a href={`mailto:${r.email}`} className="text-sky-400 hover:underline">{r.email}</a></td>
                  <td className="whitespace-nowrap px-5 py-4">{r.phone}</td>
                  <td className="whitespace-nowrap px-5 py-4"><span className="rounded-full border border-sky-400/20 bg-sky-400/[0.06] px-3 py-1 text-[11px] text-sky-300">{r.requirement}</span></td>
                  <td className="px-5 py-4">{r.positions}</td>
                  <td className="px-5 py-4">{r.location}</td>
                  <td className="max-w-[220px] truncate px-5 py-4 text-slate-400" title={r.message}>{r.message || "—"}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`enquiry-status-${i}`}
                      value={r.status || "new"}
                      onChange={(e) => updateEnquiry(r.id, e.target.value)}
                      aria-label={`Pipeline status for ${r.company}`}
                      className={`cursor-pointer rounded-full border bg-[#0A0F1D] px-3 py-1.5 text-[11px] font-semibold outline-none transition-colors duration-300 ${ENQUIRY_STATUS_CLS[r.status || "new"]}`}
                    >
                      {ENQUIRY_STATUSES.map(([v, l]) => (
                        <option key={v} value={v} className="bg-[#0A0F1D] text-slate-200">{l}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4">{rowActions("enquiries", r, i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "candidates" ? (
          <table className="w-full min-w-[1150px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {["Date", "Name", "Email", "Phone", "Location", "Experience", "Role", "Expertise", "Status", "Resume", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/[0.05] text-slate-300 transition-colors hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{fmt(r.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-white">{r.full_name}</td>
                  <td className="px-5 py-4"><a href={`mailto:${r.email}`} className="text-sky-400 hover:underline">{r.email}</a></td>
                  <td className="whitespace-nowrap px-5 py-4">{r.phone}</td>
                  <td className="px-5 py-4">{r.location}{r.preferred_location ? <span className="block text-[11px] text-slate-500">Prefers: {r.preferred_location}</span> : null}</td>
                  <td className="whitespace-nowrap px-5 py-4">{r.experience}</td>
                  <td className="px-5 py-4">{r.role}</td>
                  <td className="px-5 py-4 text-slate-400">{r.expertise || "—"}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`candidate-status-${i}`}
                      value={r.status || "new"}
                      onChange={(e) => setStatus(r.id, e.target.value)}
                      aria-label={`Pipeline status for ${r.full_name}`}
                      className={`cursor-pointer rounded-full border bg-[#0A0F1D] px-3 py-1.5 text-[11px] font-semibold outline-none transition-colors duration-300 ${STATUS_CLS[r.status || "new"]}`}
                    >
                      {STATUSES.map(([v, l]) => (
                        <option key={v} value={v} className="bg-[#0A0F1D] text-slate-200">{l}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    {r.resume_file ? (
                      <button
                        data-testid={`resume-download-${i}`}
                        onClick={() => downloadResume(r.resume_file, r.resume_name)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-[11px] font-semibold text-slate-300 transition-colors hover:border-sky-400/50 hover:text-sky-400"
                      >
                        <FileDown size={12} /> Download
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-5 py-4">{rowActions("candidates", r, i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "contacts" ? (
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {["Date", "Name", "Email", "Phone", "Message", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/[0.05] text-slate-300 transition-colors hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{fmt(r.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-white">{r.name}</td>
                  <td className="px-5 py-4"><a href={`mailto:${r.email}`} className="text-sky-400 hover:underline">{r.email}</a></td>
                  <td className="whitespace-nowrap px-5 py-4">{r.phone || "—"}</td>
                  <td className="max-w-[320px] px-5 py-4 text-slate-400">{r.message}</td>
                  <td className="px-5 py-4">{rowActions("contacts", r, i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6">
            <div className="mb-5 flex justify-end">
              <button
                data-testid="job-create-btn"
                onClick={() => setJobModal({ title: "", category: "IT", location: "", experience: "", description: "" })}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-500"
              >
                <Plus size={15} /> Post a Job
              </button>
            </div>
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {["Date", "Title", "Category", "Location", "Experience", "Applicants", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-5 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-white/[0.05] text-slate-300 transition-colors hover:bg-white/[0.02]">
                    <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{fmt(r.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className="font-medium text-white">{r.title}</span>
                      {r.description && (
                        <span className="block max-w-[280px] truncate text-xs text-slate-500" title={r.description}>{r.description}</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full border border-sky-400/20 bg-sky-400/[0.06] px-3 py-1 text-[11px] text-sky-300">{r.category}</span>
                    </td>
                    <td className="px-5 py-4">{r.location || "—"}</td>
                    <td className="px-5 py-4">{r.experience || "—"}</td>
                    <td className="px-5 py-4">
                      <span data-testid={`job-applicants-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] font-semibold text-slate-300">
                        <Users size={11} className="text-sky-400" /> {r.applicant_count ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`job-status-${i}`}
                        value={r.status || "open"}
                        onChange={(e) => toggleJob(r.id, e.target.value)}
                        aria-label={`Visibility for ${r.title}`}
                        className={`cursor-pointer rounded-full border bg-[#0A0F1D] px-3 py-1.5 text-[11px] font-semibold outline-none transition-colors duration-300 ${
                          r.status === "closed" ? "border-white/15 text-slate-400" : "border-emerald-400/40 text-emerald-300"
                        }`}
                      >
                        {JOB_STATUSES.map(([v, l]) => (
                          <option key={v} value={v} className="bg-[#0A0F1D] text-slate-200">{l}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        data-testid={`job-delete-${i}`}
                        onClick={() => deleteRow("jobs", r.id)}
                        aria-label="Delete job"
                        className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-500 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {noteModal && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 px-6 backdrop-blur-sm" onClick={() => setNoteModal(null)}>
          <div data-testid="note-modal" className="glass-card w-full max-w-lg rounded-3xl p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold text-white">Private Note</h3>
            <p className="mt-1 text-xs text-slate-500">
              {noteModal.row.full_name || noteModal.row.name} · only visible to admins
            </p>
            <textarea
              data-testid="note-modal-textarea"
              value={noteModal.note}
              onChange={(e) => setNoteModal({ ...noteModal, note: e.target.value })}
              rows={5}
              placeholder="Add context, next steps, call outcomes..."
              className="input-dark mt-5 resize-none"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                data-testid="note-modal-cancel"
                onClick={() => setNoteModal(null)}
                className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-white/30"
              >
                Cancel
              </button>
              <button
                data-testid="note-modal-save"
                onClick={saveNote}
                className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-500"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {jobModal && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 px-6 backdrop-blur-sm" onClick={() => setJobModal(null)}>
          <div data-testid="job-modal" className="glass-card w-full max-w-lg rounded-3xl p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold text-white">Post a Job Opening</h3>
            <p className="mt-1 text-xs text-slate-500">Goes live on the public Jobs page immediately</p>
            <div className="mt-5 space-y-4">
              <input data-testid="job-title-input" value={jobModal.title} onChange={(e) => setJobModal({ ...jobModal, title: e.target.value })} placeholder="Job Title *" className="input-dark" aria-label="Job Title" />
              <div className="grid grid-cols-2 gap-4">
                <select data-testid="job-category-select" value={jobModal.category} onChange={(e) => setJobModal({ ...jobModal, category: e.target.value })} className="input-dark appearance-none" aria-label="Category">
                  {["IT", "Non-IT", "Leadership"].map((c) => (
                    <option key={c} value={c} className="bg-[#0A0F1D]">{c}</option>
                  ))}
                </select>
                <input data-testid="job-location-input" value={jobModal.location} onChange={(e) => setJobModal({ ...jobModal, location: e.target.value })} placeholder="Location (e.g. Bengaluru)" className="input-dark" aria-label="Location" />
              </div>
              <input data-testid="job-experience-input" value={jobModal.experience} onChange={(e) => setJobModal({ ...jobModal, experience: e.target.value })} placeholder="Experience (e.g. 3-6 years)" className="input-dark" aria-label="Experience" />
              <textarea data-testid="job-description-input" value={jobModal.description} onChange={(e) => setJobModal({ ...jobModal, description: e.target.value })} rows={4} placeholder="Short description of the role" className="input-dark resize-none" aria-label="Description" />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="job-modal-cancel" onClick={() => setJobModal(null)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-white/30">
                Cancel
              </button>
              <button data-testid="job-modal-publish" onClick={saveJob} disabled={jobSaving} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-500 disabled:opacity-60">
                {jobSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {jobSaving ? "Posting..." : "Publish Job"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default function Admin() {
  usePageMeta("Admin — Talentify", "Private Talentify admin dashboard");
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <AdminInner />;
}
